import { useTranslation } from "@sagot/i18n";
import { Badge, Button, Icon, Input } from "@sagot/ui";
import { useState, type KeyboardEvent } from "react";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import { PII_KINDS, POLICY_KEYS, type SettingsValues } from "./settings-model";
import { SettingsCard, SwitchRow } from "./settings-ui";

const TOPICS_MAX = 50;
const RULES_MAX = 30;

function TagInput({ disabled }: { disabled: boolean }) {
  const { t } = useTranslation();
  const { control } = useFormContext<SettingsValues>();
  const [draft, setDraft] = useState("");
  return (
    <Controller
      control={control}
      name="blocked_topics"
      render={({ field }) => {
        const add = () => {
          const value = draft.trim();
          if (value && !field.value.includes(value) && field.value.length < TOPICS_MAX) field.onChange([...field.value, value]);
          setDraft("");
        };
        const onKey = (event: KeyboardEvent<HTMLInputElement>) => {
          if (event.key === "Enter") {
            event.preventDefault();
            add();
          } else if (event.key === "Backspace" && !draft && field.value.length > 0) {
            field.onChange(field.value.slice(0, -1));
          }
        };
        return (
          <div className="flex min-h-11 flex-wrap items-center gap-2 rounded-md border border-line-strong bg-surface px-2 py-2 focus-within:border-primary">
            <ul className="contents" aria-label={t("build.agentSettings.guardrails.blocked")}>
              {field.value.map((topic) => (
                <li key={topic}>
                  <Badge className="h-7 gap-1.5 pl-2.5 pr-1 text-xs">
                    {topic}
                    <button type="button" disabled={disabled} className="rounded-xs p-0.5 hover:bg-line" aria-label={t("build.agentSettings.guardrails.remove", { name: topic })} onClick={() => { field.onChange(field.value.filter((v) => v !== topic)); }}>
                      <Icon name="x" className="size-3" />
                    </button>
                  </Badge>
                </li>
              ))}
            </ul>
            <input
              aria-label={t("build.agentSettings.guardrails.blockedLabel")}
              className="h-7 min-w-40 flex-1 bg-transparent px-1 text-sm outline-none placeholder:text-fg-subtle"
              placeholder={t("build.agentSettings.guardrails.blockedPlaceholder")}
              value={draft}
              disabled={disabled}
              maxLength={200}
              onChange={(e) => { setDraft(e.target.value); }}
              onKeyDown={onKey}
              onBlur={add}
            />
          </div>
        );
      }}
    />
  );
}

function CustomRules({ disabled }: { disabled: boolean }) {
  const { t } = useTranslation();
  const form = useFormContext<SettingsValues>();
  const rules = useFieldArray({ control: form.control, name: "custom_rules" });
  return (
    <SettingsCard
      title={t("build.agentSettings.guardrails.rules")}
      hint={t("build.agentSettings.guardrails.rulesHint")}
      aside={
        <Button type="button" variant="secondary" size="sm" disabled={disabled || rules.fields.length >= RULES_MAX} onClick={() => { rules.append({ text: "" }); }}>
          <Icon name="plus" />
          {t("build.agentSettings.guardrails.addRule")}
        </Button>
      }
    >
      {rules.fields.length === 0 ? <p className="text-sm text-fg-muted">{t("build.agentSettings.guardrails.noRules")}</p> : null}
      <ol className="space-y-2">
        {rules.fields.map((rule, index) => {
          const n = index + 1;
          return (
            <li key={rule.id} className="flex items-center gap-1.5">
              <Icon name="grip-vertical" className="size-4 shrink-0 text-fg-subtle" />
              <Input aria-label={t("build.agentSettings.guardrails.rule", { n })} maxLength={2000} disabled={disabled} {...form.register(`custom_rules.${index}.text`)} />
              <Button type="button" variant="ghost" size="sm" iconOnly disabled={disabled || index === 0} aria-label={t("build.agentSettings.guardrails.moveUp", { n })} onClick={() => { rules.move(index, index - 1); }}>
                <Icon name="chevron-up" />
              </Button>
              <Button type="button" variant="ghost" size="sm" iconOnly disabled={disabled || index === rules.fields.length - 1} aria-label={t("build.agentSettings.guardrails.moveDown", { n })} onClick={() => { rules.move(index, index + 1); }}>
                <Icon name="chevron-down" />
              </Button>
              <Button type="button" variant="ghost" size="sm" iconOnly disabled={disabled} aria-label={t("build.agentSettings.guardrails.removeRule", { n })} onClick={() => { rules.remove(index); }}>
                <Icon name="trash" />
              </Button>
            </li>
          );
        })}
      </ol>
    </SettingsCard>
  );
}

export function GuardrailsTab({ disabled }: { disabled: boolean }) {
  const { t } = useTranslation();
  return (
    <>
      <SettingsCard title={t("build.agentSettings.guardrails.blocked")} hint={t("build.agentSettings.guardrails.blockedHint")}>
        <TagInput disabled={disabled} />
      </SettingsCard>
      <SettingsCard
        title={t("build.agentSettings.guardrails.pii")}
        hint={t("build.agentSettings.guardrails.piiHint")}
        aside={<Badge tone="success"><Icon name="shield-check" className="size-3" />{t("build.agentSettings.guardrails.npc")}</Badge>}
        flush
      >
        <div className="grid grid-cols-1 divide-y divide-line sm:grid-cols-2 sm:divide-y-0 [&>*]:border-line sm:[&>*:nth-child(odd)]:border-r sm:[&>*:nth-child(-n+4)]:border-b">
          {PII_KINDS.map((kind) => (
            <SwitchRow
              key={kind}
              name={`pii.${kind}`}
              label={t(`build.agentSettings.guardrails.piiKinds.${kind}`)}
              hint={<span className="font-mono">{t(`build.agentSettings.guardrails.piiExamples.${kind}`)}</span>}
              disabled={disabled}
              className="px-5 py-3.5"
            />
          ))}
        </div>
      </SettingsCard>
      <SettingsCard title={t("build.agentSettings.guardrails.safety")} flush>
        <div className="divide-y divide-line">
          <SwitchRow name="prompt_injection_guard" label={t("build.agentSettings.guardrails.injection")} hint={t("build.agentSettings.guardrails.injectionHint")} disabled={disabled} />
          <SwitchRow name="profanity_filter" label={t("build.agentSettings.guardrails.profanity")} hint={t("build.agentSettings.guardrails.profanityHint")} disabled={disabled} />
          {POLICY_KEYS.map((key) => (
            <SwitchRow key={key} name={`policies.${key}`} label={t(`build.agentSettings.guardrails.policies.${key}`)} hint={t(`build.agentSettings.guardrails.policies.${key}Hint`)} disabled={disabled} />
          ))}
        </div>
      </SettingsCard>
      <CustomRules disabled={disabled} />
    </>
  );
}
